import axios from "axios";
import { FormEvent, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useMutation, useQueryClient } from "react-query";

import { createIdempotencyKey } from "../../helpers/idempotencyKey";
import { toMinorUnits } from "../../helpers/journalAmount";
import {
  dubaiToday,
  journalPostingBounds,
} from "../../helpers/journalDateRange";
import { createJournalEntry } from "../../services/journalEntriesServices";
import { JournalEntryType, JournalGlAccount } from "../../types/journalEntries";
import styles from "./index.module.scss";

type Props = {
  accounts: JournalGlAccount[];
  show: boolean;
  onClose: () => void;
};

const messageFrom = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { errorMessage?: unknown } | undefined;
    if (typeof data?.errorMessage === "string" && data.errorMessage) {
      return data.errorMessage;
    }
  }
  return "Something went wrong. Please try again.";
};

const JournalEntryForm = ({ accounts, show, onClose }: Props) => {
  const queryClient = useQueryClient();
  const bounds = useMemo(() => journalPostingBounds(), []);
  const [glAccountCode, setGlAccountCode] = useState("");
  const [offsetGlAccountCode, setOffsetGlAccountCode] = useState("");
  const [entryType, setEntryType] = useState<JournalEntryType>("DEBIT");
  const [amount, setAmount] = useState("");
  const [entryDate, setEntryDate] = useState(dubaiToday);
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState<string>();
  const [saved, setSaved] = useState(false);
  // The same payload reuses the key while this dialog stays open, including a
  // retry after a lost response. Closing the dialog drops the attempt, so the
  // next save mints a new key. A different payload, or a successful save, does
  // the same.
  const attempt = useRef({ signature: "", key: "" });

  const mutation = useMutation(createJournalEntry, {
    onSuccess: () => {
      setSaved(true);
      setAmount("");
      setNotes("");
      attempt.current = { signature: "", key: "" };
      queryClient.invalidateQueries(["journalEntries"]);
    },
  });

  const offsets = accounts.filter((account) => account.code !== glAccountCode);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    const minor = toMinorUnits(amount);
    if (!glAccountCode || !offsetGlAccountCode) {
      setLocalError("Choose a GL account and an offset account.");
      return;
    }
    if (glAccountCode === offsetGlAccountCode) {
      setLocalError("The offset account must be a different GL account.");
      return;
    }
    if (!minor) {
      setLocalError(
        "Enter an amount greater than zero, with at most two decimal places."
      );
      return;
    }
    if (!entryDate) {
      setLocalError("Choose a posting date.");
      return;
    }
    setLocalError(undefined);
    const signature = [
      glAccountCode,
      offsetGlAccountCode,
      entryType,
      minor,
      entryDate,
      notes.trim(),
    ].join("\u001f");
    if (attempt.current.signature !== signature) {
      attempt.current = { signature, key: createIdempotencyKey() };
    }
    mutation.mutate({
      glAccountCode,
      offsetGlAccountCode,
      entryType,
      amount: minor,
      entryDate,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      idempotencyKey: attempt.current.key,
    });
  };

  const shownError =
    localError ?? (mutation.isError ? messageFrom(mutation.error) : undefined);

  const requestClose = () => {
    if (mutation.isLoading) return;
    setGlAccountCode("");
    setOffsetGlAccountCode("");
    setEntryType("DEBIT");
    setAmount("");
    setEntryDate(dubaiToday());
    setNotes("");
    setLocalError(undefined);
    setSaved(false);
    attempt.current = { signature: "", key: "" };
    mutation.reset();
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={requestClose}
      centered
      backdrop={mutation.isLoading ? "static" : true}
      keyboard={!mutation.isLoading}
      aria-labelledby="journal-entry-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="journal-entry-modal-title">
          Add journal entry
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form
          className={styles.form}
          onSubmit={onSubmit}
          data-testid="journal-entry-form"
        >
          <p className={styles.description}>
            Posts a debit and a credit for the same amount. The offset account
            takes the opposite direction.
          </p>
          <label className={styles.field}>
            GL account
            <select
              data-testid="journal-entry-gl"
              value={glAccountCode}
              onChange={(event) => {
                const next = event.target.value;
                setGlAccountCode(next);
                if (offsetGlAccountCode === next) setOffsetGlAccountCode("");
              }}
            >
              <option value="">Select an account</option>
              {accounts.map((account) => (
                <option key={account.code} value={account.code}>
                  {account.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            Direction
            <select
              data-testid="journal-entry-type"
              value={entryType}
              onChange={(event) =>
                setEntryType(event.target.value as JournalEntryType)
              }
            >
              <option value="DEBIT">Debit</option>
              <option value="CREDIT">Credit</option>
            </select>
          </label>
          <label className={styles.field}>
            Offset account
            <select
              data-testid="journal-entry-offset"
              value={offsetGlAccountCode}
              onChange={(event) => setOffsetGlAccountCode(event.target.value)}
            >
              <option value="">Select an offset</option>
              {offsets.map((account) => (
                <option key={account.code} value={account.code}>
                  {account.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            Amount (AED)
            <input
              data-testid="journal-entry-amount"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0.00"
            />
          </label>
          <label className={styles.field}>
            Posting date
            <input
              data-testid="journal-entry-date"
              type="date"
              min={bounds.min}
              max={bounds.max}
              value={entryDate}
              onChange={(event) => setEntryDate(event.target.value)}
            />
          </label>
          <label className={styles.field}>
            Notes
            <input
              data-testid="journal-entry-notes"
              value={notes}
              maxLength={500}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
          <div className={styles.actions}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              data-testid="journal-entry-cancel"
              onClick={requestClose}
              disabled={mutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="journal-entry-submit"
              disabled={mutation.isLoading}
            >
              Save journal entry
            </button>
          </div>
          {shownError && (
            <p
              className={`${styles.message} ${styles.error}`}
              data-testid="journal-entry-error"
            >
              {shownError}
            </p>
          )}
          {saved && !shownError && (
            <p className={styles.message} data-testid="journal-entry-saved">
              Journal entry saved.
            </p>
          )}
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default JournalEntryForm;
